/**
 * The security card's footer: where the vulnerability data comes from, and
 * every copyright notice and licence the shown records carry, in full
 * (docs/saidiseire-kiirus-turve-raport.md, decision 1).
 *
 * Wordfence's licence lets us store and show its records on one condition:
 * wherever a record is shown, its link, Defiant's copyright notice and the
 * licence text travel with it, and MITRE's notice and licence too when the
 * record carries CVE material. The notices are always visible; the licence
 * texts, which run to paragraphs, fold behind one button (the spec allows it)
 * and are in the page either way.
 */
import { useState } from "react";
import { ExternalLink } from "lucide-react";
import { WORDFENCE_TERMS_URL, attributionSource, type SecurityAttributionView } from "../../../../lib/monitoringSecurity";
import { Btn } from "../../../ui";
import type { T } from "../words";

export function Attributions({ attributions, t }: { attributions: SecurityAttributionView[]; t: T }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-border px-[26px] pb-5 pt-4 text-[12px]">
      <p className="crm-prow-n mb-1.5">{t("monitoring.security.sources.title")}</p>
      <p className="crm-sub">
        {t("monitoring.security.sources.wordfence")}{" "}
        <a href={WORDFENCE_TERMS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
          {t("monitoring.security.sources.terms")}
          <ExternalLink className="size-3" strokeWidth={1.8} />
        </a>
      </p>
      {attributions.length > 0 ? (
        <>
          <ul className="mt-2 flex flex-col gap-2">
            {attributions.map((a) => (
              <li key={`${a.source}:${a.notice}`} className="crm-sub">
                <span className="font-semibold">{t(`monitoring.security.sources.source.${attributionSource(a.source)}`)}: </span>
                {a.notice}
                {a.licenseUrl && /^https?:\/\//.test(a.licenseUrl) ? (
                  <>
                    {" "}
                    <a href={a.licenseUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                      {t("monitoring.security.sources.license")}
                      <ExternalLink className="size-3" strokeWidth={1.8} />
                    </a>
                  </>
                ) : null}
                {open ? <p className="crm-mut mt-1 whitespace-pre-line">{a.license}</p> : null}
              </li>
            ))}
          </ul>
          <Btn small className="mt-2" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {t(open ? "monitoring.security.sources.hide" : "monitoring.security.sources.show")}
          </Btn>
        </>
      ) : null}
    </div>
  );
}
